import React, { useEffect, useState } from 'react';
import { HoldingTableContext, IHoldingTableContext } from './HoldingTableContext';
import { useAsyncData } from '@shared/react';
import { usePagination2 } from '@features/Pagination/usePagination';
import { getLogo } from '@widgets/TransactionsTable/api';
import { getAssignedPosition } from '@widgets/HoldingTable/api';
import { useSessionStore } from '@pages/AccountDetails/store/SessionIdStore';
import { fetchStockQuotes } from '@pages/AccountDetails/api/rewrites';
import { useStockPerformanceStore } from '@pages/RIADashboard/store/stock-performance-store';

export interface ITransactionDesc {
    id: boolean;
    accountId: boolean;
    orderId: boolean;
    date: boolean;
    price: boolean;
    settlementDate: boolean;
    quantity: boolean;
    type: boolean;
    execId: boolean;
    processed: boolean;
    [key: string]: boolean;
}

export const HoldingTableProvider: React.FC<React.PropsWithChildren> = ({ children }) => {
    const [accountId, setAccountId] = useState<any>(null);
    const [sortFieldName, setSortFieldName] = useState('id');
    const { sessionId, initializeSession, refreshSession } = useSessionStore();
    const { bestPerforming, worstPerforming, setBestPerforming, setWorstPerforming } = useStockPerformanceStore();

    useEffect(() => {
        if (!sessionId) {
            initializeSession();
        }
    }, [sessionId, initializeSession]);

    const [desc, setDesc] = useState<ITransactionDesc>({
        id: true,
        accountId: true,
        orderId: true,
        date: true,
        price: true,
        settlementDate: true,
        quantity: true,
        type: true,
        execId: true,
        processed: true,
    });

    const { page, pageSize, setTotalCount, paginationOptions } = usePagination2();

    let query: any = null;

    const { data: AssignPositionBody, loading: holdingLoading } = useAsyncData(
        async () => {
            if (!sessionId) return [];
            if (accountId !== null) {
                if (accountId !== 0) {
                    query = {
                        page,
                        pageSize,
                        sortBy: `${sortFieldName} ${desc[sortFieldName] ? 'desc' : ''}`,
                        filter: `AccountId=${accountId}`,
                    };
                }
                const filterString = [query.filter].filter(Boolean).join(' , ');

                query.filter = filterString;

                const { items, totalResults } = await getAssignedPosition(query);

                const symbols = items?.map((stock: any) => stock.security.symbol).join(',');
                const result = await fetchStockQuotes(symbols || '', sessionId || '');

                if (result === null) {
                    await refreshSession();
                    return;
                }

                type StockInfo = {
                    lastPrice: number;
                    longname?: string;
                };

                const stockMap = new Map<string, StockInfo>(
                    result.quotedata.map((stock: any) => [
                        stock.symbol,
                        {
                            lastPrice: stock.pricedata?.last ?? 0,
                            longname: stock.longname,
                        },
                    ]),
                );

                setTotalCount(totalResults);

                // 🚀 Reset best and worst performing stocks before recalculating
                setBestPerforming(null);
                setWorstPerforming(null);

                let bestStock: any = null;
                let worstStock: any = null;

                const updatedData: any[] = (items ?? []).map((stock: any) => {
                    const stockInfo = stockMap.get(stock.security.symbol);
                    const latestPrice = stockInfo?.lastPrice ?? stock.buyingPrice;
                    const totalValue = latestPrice * stock.quantity;
                    const investmentReturn = totalValue - stock.quantity * stock.averagePrice;

                    if (stock.quantity > 0) {
                        const returnPercentage = (investmentReturn / (stock.quantity * stock.averagePrice)) * 100;

                        // Check for best-performing stock
                        if (!bestStock || returnPercentage > bestStock.percentage) {
                            bestStock = {
                                name: stock.security.symbol,
                                symbol: stock.security.symbol,
                                percentage: returnPercentage,
                            };
                        }

                        // Check for worst-performing stock
                        if (!worstStock || returnPercentage < worstStock.percentage) {
                            worstStock = {
                                name: stock.security.symbol,
                                symbol: stock.security.symbol,
                                percentage: returnPercentage,
                            };
                        }
                    }

                    return {
                        name: stockInfo?.longname ?? stock.security.description,
                        symbol: stock.security.symbol,
                        icon: '',
                        quantity: stock.quantity,
                        marketPrice: `$${latestPrice.toFixed(2)}`,
                        isPositive: investmentReturn >= 0,
                        investmentReturn: `$${investmentReturn.toFixed(2)}`,
                    };
                });

                // 🚀 Update best and worst performing stocks only if valid stocks exist
                if (bestStock) setBestPerforming(bestStock);
                if (worstStock) setWorstPerforming(worstStock);

                return updatedData || [];
            } else {
                return [];
            }
        },
        [],
        [pageSize, page, accountId, sessionId],
    );

    const { data: arrayWithLogos, loading: logoLoading } = useAsyncData(
        async () => {
            if (!AssignPositionBody) return [];
            return await Promise.all(
                AssignPositionBody.map(async (item: any) => {
                    const res = await getLogo({ symbol: item.symbol });
                    return { link: res, symbol: item.symbol };
                }),
            );
        },
        [],
        [AssignPositionBody],
    );

    const value: IHoldingTableContext = {
        AssignPositionBody: AssignPositionBody ?? [],
        paginationOptions,
        arrayWithLogos,
        holdingLoading,
        sortFieldName,
        desc,
        setAccountId,
    };

    return <HoldingTableContext.Provider value={value}>{children}</HoldingTableContext.Provider>;
};
